import { Play, Pause, Volume2, VolumeX, SkipForward } from 'lucide-react';
import type { Phase } from '@ringmaster/shared';

/**
 * RingmasterBar — host-only floating control bar, fixed to the bottom.
 *
 * Anatomy: circular gold-tinted pause/play button (left) · "Ringmaster" over a
 * status line (center) · volume/mute speaker icon (right). Rounded, dark
 * translucent, floats above content with a soft top fade.
 *
 * Playback control is wired to the narration audio (stagePlayer):
 *  - pause/play toggles the AudioContext (suspend/resume)
 *  - volume icon toggles mute
 *
 * The status line REFLECTS what the game is doing: "Narrating…" while a clip
 * plays, otherwise a phase-specific waiting line ("Werewolves are choosing…",
 * "Discussion…") — never a stale "Speaking…" that outlives the audio.
 */

export interface RingmasterBarProps {
  paused: boolean;
  muted: boolean;
  /** True while narration audio is actively speaking. */
  speaking: boolean;
  /** Current public phase — drives the idle (non-speaking) status line. */
  phase?: Phase;
  onTogglePause: () => void;
  onToggleMute: () => void;
  /**
   * Host-only: end the current phase early (HOST_ADVANCE). When provided AND the
   * phase is DISCUSS, a Skip button appears so the host can end the discussion
   * without waiting out the timer. Omit to render no skip control.
   */
  onSkipPhase?: () => void;
}

/** What the Ringmaster is waiting on in a given phase (when not narrating). */
function waitingStatus(phase: Phase | undefined): string {
  switch (phase) {
    case 'LOBBY':
      return 'Waiting to start';
    case 'DEAL':
      return 'Players getting ready…';
    case 'NIGHT_INTRO':
    case 'RESOLVE_NIGHT':
      return 'Night falls…';
    case 'NIGHT_WOLVES':
      return 'Werewolves are choosing…';
    case 'NIGHT_INTERLUDE':
      return 'The village sleeps…';
    case 'NIGHT_SEER':
      return 'The Seer is watching…';
    case 'DAWN':
      return 'Dawn breaks…';
    case 'DISCUSS':
      return 'Discussion…';
    case 'VOTE':
      return 'Voting…';
    case 'RESOLVE_VOTE':
      return 'The verdict…';
    // ── The Whisper ────────────────────────────────────────────────────────────
    case 'WHISPER_INTRO':
      return 'The Seer chooses a word…';
    case 'QUESTION':
      return 'The Seer is listening…';
    case 'RESOLVE_GUESS':
      return 'The Seer reads…';
    case 'HUNT':
      return 'Naming the Deceiver…';
    case 'RESOLVE_HUNT':
      return 'The mask falls…';
    case 'SCORE':
      return 'Tallying the round…';
    case 'END':
      return 'Game over';
    default:
      return 'Waiting…';
  }
}

export function RingmasterBar({
  paused,
  muted,
  speaking,
  phase,
  onTogglePause,
  onToggleMute,
  onSkipPhase,
}: RingmasterBarProps) {
  const status = paused ? 'Paused' : speaking ? 'Narrating…' : waitingStatus(phase);
  // The host may unilaterally end the discussion — only during DISCUSS, and only
  // when the surface wired a skip handler. We never expose skipping other phases.
  const showSkip = onSkipPhase !== undefined && phase === 'DISCUSS';

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'center',
        // Soft top fade so content scrolls behind it gracefully.
        paddingTop: 24,
        paddingBottom: 'max(var(--space-4), env(safe-area-inset-bottom, 0px) + var(--space-2))',
        paddingLeft: 'var(--space-4)',
        paddingRight: 'var(--space-4)',
        background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 100%)',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 420,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: '10px 14px',
          borderRadius: 'var(--radius-pill)',
          background: 'rgba(20, 8, 4, 0.72)',
          border: '1px solid rgba(126, 92, 68, 0.35)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}
      >
        {/* Pause / play */}
        <button
          type="button"
          className="rm-hover"
          onClick={onTogglePause}
          aria-label={paused ? 'Resume narration' : 'Pause narration'}
          style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: '1px solid rgba(201, 164, 78, 0.5)',
            background: 'radial-gradient(circle at 50% 40%, rgba(201,164,78,0.28) 0%, rgba(126,92,68,0.18) 100%)',
            color: '#f2d9a0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {paused ? <Play size={20} fill="currentColor" /> : <Pause size={20} fill="currentColor" />}
        </button>

        {/* Label + status */}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 14,
              color: 'var(--text-secondary)',
              lineHeight: 1.1,
            }}
          >
            Ringmaster
          </div>
          <div
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {status}
          </div>
        </div>

        {/* Skip discussion (host-only, DISCUSS-only) */}
        {showSkip && (
          <button
            type="button"
            className="rm-hover"
            onClick={onSkipPhase}
            aria-label="Skip the discussion"
            title="Skip the discussion"
            style={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '1px solid rgba(201, 164, 78, 0.5)',
              background: 'radial-gradient(circle at 50% 40%, rgba(201,164,78,0.28) 0%, rgba(126,92,68,0.18) 100%)',
              color: '#f2d9a0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <SkipForward size={20} fill="currentColor" />
          </button>
        )}

        {/* Volume / mute */}
        <button
          type="button"
          className="rm-hover"
          onClick={onToggleMute}
          aria-label={muted ? 'Unmute narration' : 'Mute narration'}
          style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: 'none',
            background: 'transparent',
            color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
        </button>
      </div>
    </div>
  );
}
